import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Server, 
  Package, 
  Plus, 
  Play, 
  Square, 
  RefreshCw, 
  Trash, 
  Terminal, 
  Box, 
  Settings,
  Cloud,
  CpuIcon
} from 'lucide-react';
import { 
  ContainerType, 
  VMType, 
  OSType, 
  DeploymentStatus
} from '@/components/dashboard/network/NetworkTypes';

interface Deployment {
  id: string;
  name: string;
  type: 'vm' | 'container';
  status: DeploymentStatus;
  created: string;
}

interface VMDeployment extends Deployment {
  type: 'vm';
  vmType: VMType;
  os: OSType;
  specs: {
    cpu: number;
    memory: number;
    storage: number;
  };
  ip: string;
}

interface ContainerDeployment extends Deployment {
  type: 'container';
  containerType: ContainerType;
  image: string;
  resources: {
    cpu: number;
    memory: number;
  };
}

interface DeploymentCenterProps {
  initialTask?: string | null;
}

// Sample deployments
const sampleDeployments: (VMDeployment | ContainerDeployment)[] = [
  {
    id: 'vm-1',
    name: 'API Server',
    type: 'vm',
    vmType: 'kvm',
    os: 'ubuntu',
    status: 'running',
    created: '2023-04-15',
    specs: { cpu: 2, memory: 4, storage: 80 },
    ip: '10.0.1.5'
  },
  {
    id: 'container-1',
    name: 'Web Frontend',
    type: 'container',
    containerType: 'docker',
    image: 'nginx:latest',
    status: 'running',
    created: '2023-05-20',
    resources: { cpu: 0.5, memory: 1 }
  },
  {
    id: 'vm-2',
    name: 'Database Server',
    type: 'vm',
    vmType: 'kvm',
    os: 'ubuntu',
    status: 'stopped',
    created: '2023-03-10',
    specs: { cpu: 4, memory: 8, storage: 500 },
    ip: '10.0.1.6'
  },
  {
    id: 'container-2',
    name: 'Cache Service',
    type: 'container',
    containerType: 'kubernetes',
    image: 'redis:alpine',
    status: 'running',
    created: '2023-06-05',
    resources: { cpu: 0.5, memory: 1 }
  }
];

const DeploymentCenter: React.FC<DeploymentCenterProps> = ({ initialTask }) => {
  const [deployments, setDeployments] = useState<(VMDeployment | ContainerDeployment)[]>(sampleDeployments);
  const [deploymentType, setDeploymentType] = useState<'vm' | 'container'>('vm');
  const [showNewForm, setShowNewForm] = useState(false);
  
  const [newVM, setNewVM] = useState<Omit<VMDeployment, 'id' | 'status' | 'created'>>({
    name: '',
    type: 'vm',
    vmType: 'kvm',
    os: 'ubuntu',
    specs: { cpu: 2, memory: 4, storage: 100 },
    ip: ''
  });
  
  const [newContainer, setNewContainer] = useState<Omit<ContainerDeployment, 'id' | 'status' | 'created'>>({
    name: '',
    type: 'container',
    containerType: 'docker',
    image: '',
    resources: { cpu: 0.5, memory: 1 }
  });

  // Process initialTask when component mounts
  useEffect(() => {
    if (initialTask && initialTask.toLowerCase().includes('deploy')) {
      // If task mentions deployment, show the form
      setShowNewForm(true);
      
      // Try to determine if it's a VM or container deployment
      if (initialTask.toLowerCase().includes('vm') || 
          initialTask.toLowerCase().includes('virtual machine')) {
        setDeploymentType('vm');
        
        // Try to extract a name from the task
        const words = initialTask.split(' ');
        const nameIndex = words.findIndex(word => 
          word.toLowerCase() === 'vm' || 
          word.toLowerCase() === 'server'
        );
        
        if (nameIndex >= 0 && nameIndex < words.length - 1) {
          setNewVM(prev => ({...prev, name: words[nameIndex + 1]}));
        }
      } 
      else if (initialTask.toLowerCase().includes('container') || 
               initialTask.toLowerCase().includes('docker') ||
               initialTask.toLowerCase().includes('kubernetes')) {
        setDeploymentType('container');
        
        // Try to extract container details
        if (initialTask.toLowerCase().includes('nginx')) {
          setNewContainer(prev => ({
            ...prev, 
            name: 'Web Server',
            image: 'nginx:latest'
          }));
        } else if (initialTask.toLowerCase().includes('redis')) {
          setNewContainer(prev => ({
            ...prev, 
            name: 'Cache Service',
            image: 'redis:alpine'
          }));
        }
      }
      
      toast.info('AI preparing deployment', {
        description: 'The system is preparing a deployment based on your request.'
      });
    }
  }, [initialTask]);

  const handleDeleteDeployment = (id: string) => {
    setDeployments(deployments.filter(d => d.id !== id));
    toast.success("Deployment deleted", {
      description: "The deployment has been removed from your infrastructure."
    });
  };

  const handleToggleStatus = (id: string) => {
    setDeployments(deployments.map(d => 
      d.id === id 
        ? { ...d, status: d.status === 'running' ? 'stopped' : 'running' } 
        : d
    ));
    
    const deployment = deployments.find(d => d.id === id);
    toast.success(
      `Deployment ${deployment?.status === 'running' ? 'stopped' : 'started'}`,
      {
        description: `${deployment?.name} has been ${deployment?.status === 'running' ? 'stopped' : 'started'}.`
      }
    );
  };

  const handleCreateVM = () => {
    const newId = `vm-${Date.now().toString().slice(-4)}`;
    const createdVM: VMDeployment = {
      id: newId,
      ...newVM,
      status: 'pending',
      created: new Date().toISOString().split('T')[0]
    };
    
    setDeployments([...deployments, createdVM]);
    setShowNewForm(false);
    setNewVM({
      name: '',
      type: 'vm',
      vmType: 'kvm',
      os: 'ubuntu',
      specs: { cpu: 2, memory: 4, storage: 100 },
      ip: ''
    });
    
    toast.success("VM deployment initiated", {
      description: `${newVM.name} is being provisioned. It will be available shortly.`
    });
    
    // Simulate deployment completion after a delay
    setTimeout(() => {
      setDeployments(prev => prev.map(d => 
        d.id === newId ? { ...d, status: 'running' } : d
      ));
      
      toast.success("VM deployment complete", {
        description: `${newVM.name} is now running and ready to use.`
      });
    }, 3000);
  };

  const handleCreateContainer = () => {
    const newId = `container-${Date.now().toString().slice(-4)}`;
    const createdContainer: ContainerDeployment = {
      id: newId,
      ...newContainer,
      status: 'pending',
      created: new Date().toISOString().split('T')[0]
    };
    
    setDeployments([...deployments, createdContainer]);
    setShowNewForm(false);
    setNewContainer({
      name: '',
      type: 'container',
      containerType: 'docker',
      image: '',
      resources: { cpu: 0.5, memory: 1 }
    });
    
    toast.success("Container deployment initiated", {
      description: `${newContainer.name} is being deployed. It will be available shortly.`
    });
    
    // Simulate deployment completion after a delay
    setTimeout(() => {
      setDeployments(prev => prev.map(d => 
        d.id === newId ? { ...d, status: 'running' } : d
      ));
      
      toast.success("Container deployment complete", {
        description: `${newContainer.name} is now running and ready to use.`
      });
    }, 2000);
  };

  const renderDeploymentIcon = (deployment: VMDeployment | ContainerDeployment) => {
    if (deployment.type === 'vm') {
      return <Server className="h-5 w-5" />;
    } else {
      return <Package className="h-5 w-5" />;
    }
  };

  const renderStatusBadge = (status: DeploymentStatus) => {
    let color = '';
    let icon = null;
    
    switch(status) {
      case 'running':
        color = 'bg-green-500/10 text-green-500 border-green-500/20';
        icon = <Play className="h-3 w-3" />;
        break;
      case 'stopped':
        color = 'bg-gray-500/10 text-gray-500 border-gray-500/20';
        icon = <Square className="h-3 w-3" />;
        break;
      case 'pending':
        color = 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
        icon = <RefreshCw className="h-3 w-3 animate-spin" />;
        break;
      case 'failed':
        color = 'bg-red-500/10 text-red-500 border-red-500/20';
        icon = <Trash className="h-3 w-3" />;
        break;
    }
    
    return (
      <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${color}`}>
        {icon}
        <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
      </div>
    );
  };

  const handleRestartDeployment = (id: string) => {
    const deployment = deployments.find(d => d.id === id);
    
    if (deployment && deployment.status === 'running') {
      // First set to stopped
      setDeployments(prev => prev.map(d => 
        d.id === id ? { ...d, status: 'stopped' } : d
      ));
      
      toast.info(`Restarting ${deployment.name}...`);
      
      // Then after a short delay, set to pending
      setTimeout(() => {
        setDeployments(prev => prev.map(d => 
          d.id === id ? { ...d, status: 'pending' } : d
        ));
        
        // Finally after another delay, set to running
        setTimeout(() => {
          setDeployments(prev => prev.map(d => 
            d.id === id ? { ...d, status: 'running' } : d
          ));
          
          toast.success(`${deployment.name} restarted successfully`);
        }, 1500);
      }, 1000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold flex items-center">
          <Server className="mr-2 h-5 w-5" />
          Deployment Center
        </h2>
        <Button onClick={() => setShowNewForm(!showNewForm)}>
          <Plus className="h-4 w-4 mr-2" />
          New Deployment
        </Button>
      </div>
      
      {showNewForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Deployment</CardTitle>
            <CardDescription>Deploy a new virtual machine or container</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={deploymentType} onValueChange={(v) => setDeploymentType(v as 'vm' | 'container')}>
              <TabsList className="mb-4 grid grid-cols-2">
                <TabsTrigger value="vm" className="flex items-center">
                  <Server className="mr-2 h-4 w-4" />
                  Virtual Machine
                </TabsTrigger>
                <TabsTrigger value="container" className="flex items-center">
                  <Package className="mr-2 h-4 w-4" />
                  Container
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="vm">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="vm-name">VM Name</Label>
                    <Input 
                      id="vm-name" 
                      value={newVM.name} 
                      onChange={(e) => setNewVM({...newVM, name: e.target.value})}
                      placeholder="e.g. api-server"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="vm-type">VM Type</Label>
                      <Select 
                        value={newVM.vmType} 
                        onValueChange={(value) => setNewVM({...newVM, vmType: value as VMType})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select VM type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kvm">KVM</SelectItem>
                          <SelectItem value="hyperv">Hyper-V</SelectItem>
                          <SelectItem value="vmware">VMware</SelectItem>
                          <SelectItem value="xen">Xen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid gap-2">
                      <Label htmlFor="vm-os">Operating System</Label>
                      <Select 
                        value={newVM.os} 
                        onValueChange={(value) => setNewVM({...newVM, os: value as OSType})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select OS" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ubuntu">Ubuntu</SelectItem>
                          <SelectItem value="debian">Debian</SelectItem>
                          <SelectItem value="centos">CentOS</SelectItem>
                          <SelectItem value="windows">Windows</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="vm-cpu">CPU Cores</Label>
                      <Select 
                        value={newVM.specs.cpu.toString()} 
                        onValueChange={(value) => setNewVM({
                          ...newVM, 
                          specs: {...newVM.specs, cpu: parseInt(value)}
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="CPU Cores" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 Core</SelectItem>
                          <SelectItem value="2">2 Cores</SelectItem>
                          <SelectItem value="4">4 Cores</SelectItem>
                          <SelectItem value="8">8 Cores</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid gap-2">
                      <Label htmlFor="vm-memory">Memory (GB)</Label>
                      <Select 
                        value={newVM.specs.memory.toString()} 
                        onValueChange={(value) => setNewVM({
                          ...newVM, 
                          specs: {...newVM.specs, memory: parseInt(value)}
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Memory" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2">2 GB</SelectItem>
                          <SelectItem value="4">4 GB</SelectItem>
                          <SelectItem value="8">8 GB</SelectItem>
                          <SelectItem value="16">16 GB</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid gap-2">
                      <Label htmlFor="vm-storage">Storage (GB)</Label>
                      <Select 
                        value={newVM.specs.storage.toString()} 
                        onValueChange={(value) => setNewVM({
                          ...newVM, 
                          specs: {...newVM.specs, storage: parseInt(value)}
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Storage" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="20">20 GB</SelectItem>
                          <SelectItem value="50">50 GB</SelectItem>
                          <SelectItem value="100">100 GB</SelectItem>
                          <SelectItem value="250">250 GB</SelectItem>
                          <SelectItem value="500">500 GB</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="container">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="container-name">Container Name</Label>
                    <Input 
                      id="container-name" 
                      value={newContainer.name} 
                      onChange={(e) => setNewContainer({...newContainer, name: e.target.value})}
                      placeholder="e.g. web-server"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="container-type">Container Type</Label>
                      <Select 
                        value={newContainer.containerType} 
                        onValueChange={(value) => setNewContainer({...newContainer, containerType: value as ContainerType})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select container type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="docker">Docker</SelectItem>
                          <SelectItem value="kubernetes">Kubernetes</SelectItem>
                          <SelectItem value="podman">Podman</SelectItem>
                          <SelectItem value="lxc">LXC</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid gap-2">
                      <Label htmlFor="container-image">Image</Label>
                      <Input 
                        id="container-image" 
                        value={newContainer.image} 
                        onChange={(e) => setNewContainer({...newContainer, image: e.target.value})}
                        placeholder="e.g. nginx:latest"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="container-cpu">CPU Allocation</Label>
                      <Select 
                        value={newContainer.resources.cpu.toString()} 
                        onValueChange={(value) => setNewContainer({
                          ...newContainer, 
                          resources: {...newContainer.resources, cpu: parseFloat(value)}
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="CPU Allocation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0.5">0.5 Core</SelectItem>
                          <SelectItem value="1">1 Core</SelectItem>
                          <SelectItem value="2">2 Cores</SelectItem>
                          <SelectItem value="4">4 Cores</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid gap-2">
                      <Label htmlFor="container-memory">Memory (GB)</Label>
                      <Select 
                        value={newContainer.resources.memory.toString()} 
                        onValueChange={(value) => setNewContainer({
                          ...newContainer, 
                          resources: {...newContainer.resources, memory: parseFloat(value)}
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Memory" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0.5">0.5 GB</SelectItem>
                          <SelectItem value="1">1 GB</SelectItem>
                          <SelectItem value="2">2 GB</SelectItem>
                          <SelectItem value="4">4 GB</SelectItem>
                          <SelectItem value="8">8 GB</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={() => setShowNewForm(false)}>Cancel</Button>
            {deploymentType === 'vm' ? (
              <Button 
                onClick={handleCreateVM} 
                disabled={!newVM.name}
              >
                Deploy VM
              </Button>
            ) : (
              <Button 
                onClick={handleCreateContainer} 
                disabled={!newContainer.name || !newContainer.image}
              >
                Deploy Container
              </Button>
            )}
          </CardFooter>
        </Card>
      )}
      
      <div className="grid gap-4 md:grid-cols-2">
        {deployments.map((deployment) => (
          <Card key={deployment.id} className={deployment.status === 'stopped' ? 'opacity-70' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center">
                  {renderDeploymentIcon(deployment)}
                  <span className="ml-2">{deployment.name}</span>
                </div>
                <div>
                  {renderStatusBadge(deployment.status)}
                </div>
              </CardTitle>
              <CardDescription className="flex justify-between items-center">
                {deployment.type === 'vm' ? (
                  <span>{deployment.os.charAt(0).toUpperCase() + deployment.os.slice(1)} VM ({deployment.vmType.toUpperCase()})</span>
                ) : (
                  <span>{deployment.containerType.charAt(0).toUpperCase() + deployment.containerType.slice(1)} Container</span>
                )}
                <span className="text-xs">{deployment.created}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-2">
              <div className="text-sm space-y-2">
                {deployment.type === 'vm' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">IP Address:</span>
                      <span className="font-mono">{deployment.ip}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Resources:</span>
                      <span>
                        {deployment.specs.cpu} CPU, {deployment.specs.memory}GB RAM, {deployment.specs.storage}GB
                      </span>
                    </div>
                  </>
                )}
                
                {deployment.type === 'container' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Image:</span>
                      <span className="font-mono">{deployment.image}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Resources:</span>
                      <span>
                        {deployment.resources.cpu} CPU, {deployment.resources.memory}GB RAM
                      </span>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-between gap-2 pt-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1 h-8"
                onClick={() => handleToggleStatus(deployment.id)}
                disabled={deployment.status === 'pending' || deployment.status === 'failed'}
              >
                {deployment.status === 'running' ? (
                  <>
                    <Square className="h-3 w-3 mr-1" />
                    Stop
                  </>
                ) : (
                  <>
                    <Play className="h-3 w-3 mr-1" />
                    Start
                  </>
                )}
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1 h-8"
                onClick={() => handleRestartDeployment(deployment.id)}
                disabled={deployment.status !== 'running'}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Restart
              </Button>
              
              {deployment.type === 'vm' ? (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1 h-8"
                  disabled={deployment.status !== 'running'}
                  onClick={() => 
                    toast.info("SSH Terminal opened", {
                      description: "Opening SSH connection to the VM in a new terminal."
                    })
                  }
                >
                  <Terminal className="h-3 w-3 mr-1" />
                  SSH
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1 h-8"
                  disabled={deployment.status !== 'running'}
                  onClick={() => 
                    toast.info("Container logs opened", {
                      description: "Opening container logs viewer."
                    })
                  }
                >
                  <Terminal className="h-3 w-3 mr-1" />
                  Logs
                </Button>
              )}
              
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 aspect-square p-0"
                onClick={() => handleDeleteDeployment(deployment.id)}
              >
                <Trash className="h-3 w-3" />
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default DeploymentCenter;
